import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';

interface PdpDesktopDetailRowProps {
  label: string;
  value: ReactNode;
  tooltip?: string;
  onInfoIconClick?: (label: string) => void;
}

export function PdpDesktopDetailRow({
  label,
  value,
  tooltip,
  onInfoIconClick,
}: PdpDesktopDetailRowProps) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="flex items-center justify-between border-t border-[#eee] px-4 py-2.5 text-xs">
      <div ref={containerRef} className="relative text-[#5b7da3]">
        <span className="inline-flex items-center gap-1">
          {label}
          {tooltip ? (
            <button
              type="button"
              className="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[#999]"
              aria-label={`More information about ${label}`}
              aria-expanded={open}
              aria-controls={tooltipId}
              onClick={() => {
                setOpen((prev) => !prev);
                onInfoIconClick?.(label);
              }}
            >
              <Info size={14} strokeWidth={1.75} aria-hidden />
            </button>
          ) : null}
        </span>
        {tooltip && open ? (
          <div
            id={tooltipId}
            role="tooltip"
            className="absolute bottom-[calc(100%+8px)] left-0 z-[11] w-[140px] bg-white px-3 py-2 text-left text-xs leading-[15px] text-[#555] shadow-[0_0_6px_1px_rgb(0_0_0/10%)]"
          >
            {tooltip}
          </div>
        ) : null}
      </div>
      <div className="text-right font-normal text-[#334e68]">{value}</div>
    </div>
  );
}
